<template>
  <div class="upload-panel">
    <div class="upload-panel__header">
      <h1 class="upload-panel__title">
        参考视频/音频 <span class="upload-panel__title-accent">找音乐</span>
      </h1>
    </div>

    <p v-if="sizeError" class="upload-panel__size-error">
      文件上传失败，最大不得超过150MB。
    </p>

    <div
      class="upload-panel__box"
      :class="{
        'is-dragover': isDragover && isIdle,
        'is-uploading': !isIdle
      }"
      @dragenter.prevent="onDragEnter"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <template v-if="isIdle">
        <textarea
          v-model="keyword"
          class="upload-panel__input"
          rows="4"
          placeholder="输入关键词/音乐ID 查找音乐 也可添加视频/音频，识别和推荐相似BGM，支持上传和拖拽"
          @keydown.enter.exact.prevent="handleSubmit"
        />

        <div class="upload-panel__toolbar">
          <button
            type="button"
            class="upload-panel__action-btn"
            @click="triggerUpload"
          >
            <i class="el-icon-paperclip"></i>
            <span>上传音频/视频找音乐</span>
          </button>

          <button
            type="button"
            class="upload-panel__submit"
            title="搜索"
            @click="handleSubmit"
          >
            <svg
              class="upload-panel__wave-icon"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M4 10v4M7 7v10M10 4v16M13 6v12M16 8v8M19 10v4"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>
      </template>

      <div v-else class="upload-panel__progress">
        <div class="upload-panel__file-badge">
          <span class="upload-panel__file-icon">
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <rect x="4" y="2" width="16" height="20" rx="2" fill="#fff" />
              <path
                d="M12 7.5c-1.8 0-2.7 1.1-2.7 2.4 0 1.7 1.4 2.4 2.7 3.1 1.3-.7 2.7-1.4 2.7-3.1 0-1.3-.9-2.4-2.7-2.4zm-2.2 9.2v-1.6c0-.7.4-1.2 1.1-1.5.4.2.8.4 1.1.5.3-.1.7-.3 1.1-.5.7.3 1.1.8 1.1 1.5v1.6H9.8z"
                fill="#e52a0d"
              />
            </svg>
          </span>
          <span class="upload-panel__file-name" :title="uploadFileName">{{ uploadFileName }}</span>
        </div>

        <div class="upload-panel__status-row">
          <i
            v-if="status === 'uploading' || status === 'parsing'"
            class="el-icon-loading upload-panel__spinner"
          />
          <i
            v-else
            class="el-icon-success upload-panel__spinner upload-panel__spinner--done"
          />
          <span class="upload-panel__status-text">{{ statusText }}</span>
        </div>

        <p class="upload-panel__hint">
          大文件可能需要 <em>30 - 60</em> 秒，请耐心等待不要关闭页面
        </p>
      </div>

      <input
        ref="fileInput"
        class="upload-panel__file"
        type="file"
        :accept="accept"
        :disabled="!isIdle"
        @change="onFileChange"
      />
    </div>

    <div class="upload-panel__footer">
      <label class="upload-panel__agree">
        <el-checkbox v-model="agreed" :disabled="!isIdle" />
        <span>我已知晓并同意<a href="https://qkodo.playlistmusic.com.cn/assets/common/playlistmusic-user-agreement.pdf" target="_blank">《用户服务协议》</a></span>
      </label>
      <button type="button" class="upload-panel__tips" @click="tipsVisible = true">
        <i class="el-icon-question"></i>
        AI识曲tips
      </button>
    </div>

    <el-dialog
      title="AI识曲TIPS"
      :visible.sync="tipsVisible"
      width="540px"
      custom-class="upload-panel-tips-dialog"
      append-to-body
      :close-on-click-modal="true"
    >
      <ol class="upload-panel__tips-list">
        <li>支持上传音频、视频文件（视频文件需≤150M）。</li>
        <li>您可在上传文件的时间轴中，选取5~60秒的音乐片段，系统将基于该片段的风格、节奏与旋律特征，为您推荐相同或相近的音乐（建议您选取15~30秒片段，识别效果更佳）。</li>
        <li>您上传的文件系统将保留7天，方便您再次识别及推荐音乐。遇到满意的音乐可点击右侧♥进行收藏，便于后续查看与管理。</li>
        <li>如遇到任何问题，或需要人工选曲，请联系在线客服，或致电13522352793（微信同号），我们将竭诚为您服务，感谢！</li>
      </ol>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="tipsVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getQNToken } from '@/api/qiniu'
import { fetchSmlAdd, fetchSmlGet } from '@/api/similarAI'
import { getFileName, getTimeRandom, random_string } from '@/utils/utils'

const QINIU_UPLOAD_URL = 'https://upload-z1.qiniup.com'
const FILE_MAX = 150 * 1024 * 1024
const POLL_INTERVAL = 5000
const AUDIO_EXTS = ['mp3', 'wav', 'aac', 'm4a', 'flac', 'ogg', 'wma']
const VIDEO_EXTS = ['mp4', 'avi', 'flv', 'wmv', 'mov', 'mkv', 'webm']

export default {
  name: 'UploadPanel',
  data() {
    return {
      keyword: '',
      agreed: true,
      isDragover: false,
      dragCounter: 0,
      status: 'idle',
      uploadPercent: 0,
      uploadFileName: '',
      uploadedKey: '',
      uploadXhr: null,
      sizeError: false,
      pollTimer: null,
      pollLogId: '',
      tipsVisible: false,
      accept: 'audio/*,video/*,.mp3,.mp4,.wav,.avi,.flv,.wmv,.mov,.aac,.m4a,.flac,.ogg,.wma,.mkv,.webm'
    }
  },
  computed: {
    isIdle() {
      return this.status === 'idle'
    },
    statusText() {
      if (this.status === 'parsing') {
        return '正在解析中...'
      }
      if (this.status === 'success') {
        return '上传完成'
      }
      return `正在上传中... ${this.uploadPercent}%`
    }
  },
  beforeDestroy() {
    this.clearPoll()
    if (this.uploadXhr) {
      this.uploadXhr.abort()
      this.uploadXhr = null
    }
  },
  methods: {
    getExt(name) {
      const parts = (name || '').split('.')
      return parts.length > 1 ? parts.pop().toLowerCase() : ''
    },
    isAudioOrVideo(file) {
      const ext = this.getExt(file.name)
      const byExt = AUDIO_EXTS.includes(ext) || VIDEO_EXTS.includes(ext)
      const byMime = /^(audio|video)\//.test(file.type || '')
      return byExt || byMime
    },
    validateFile(file) {
      if (!file) return false

      if (!this.isAudioOrVideo(file)) {
        this.sizeError = false
        this.$message.error('仅支持音频或视频格式文件')
        return false
      }

      if (file.size > FILE_MAX) {
        this.sizeError = true
        return false
      }

      this.sizeError = false

      if (!this.agreed) {
        this.$message.warning('请先同意用户服务协议')
        return false
      }

      return true
    },
    clearPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
      this.pollLogId = ''
    },
    resetIdle() {
      this.clearPoll()
      this.status = 'idle'
      this.uploadPercent = 0
      this.uploadFileName = ''
      this.isDragover = false
      this.dragCounter = 0
      this.uploadXhr = null
    },
    handleFile(file) {
      if (!this.isIdle) return
      if (!this.validateFile(file)) return
      this.startUpload(file)
    },
    async startUpload(file) {
      this.clearPoll()
      this.status = 'uploading'
      this.uploadPercent = 0
      this.uploadFileName = file.name
      this.uploadedKey = ''

      try {
        const res = await getQNToken({
          upload_type: 14,
          is_pvt: 0
        })
        const payload = res.data
        const fileSuffix = getFileName(file.name)
        const randomName = `${getTimeRandom()}${random_string()}${fileSuffix}`
        const key = `${payload.config.bucket}/${payload.dir}${randomName}`
        const token = payload.token

        await this.uploadToQiniu(file, token, key)
        this.uploadPercent = 100
        this.uploadedKey = key
        await this.reportAndPoll(key, file.name)
      } catch (err) {
        console.error(err)
        this.$message.error((err && err.message) || '上传失败，请重试')
        this.resetIdle()
      }
    },
    async reportAndPoll(fileKey, fileName) {
      this.status = 'parsing'
      const rollRes = await fetchSmlAdd({
        file_key: fileKey,
        file_name: fileName || this.uploadFileName || ''
      })
      const logId = rollRes && rollRes.data && rollRes.data.sml_id
      if (!logId) {
        throw new Error('未获取到 log_id')
      }
      this.pollLogId = logId
      await this.pollStatOnce(logId)
      // 已完成跳转或失败回 idle，不再启动定时轮询
      if (this.status !== 'parsing') return
      this.pollTimer = setInterval(() => {
        this.pollStatOnce(logId)
      }, POLL_INTERVAL)
    },
    async pollStatOnce(logId) {
      if (!logId || this.pollLogId !== logId) return
      try {
        const res = await fetchSmlGet({ sml_id: logId })
        const data = (res && res.data) || {}
        if (Number(data.is_finish) === 1) {
          this.clearPoll()
          this.status = 'success'
          this.$router.push({
            path: `/similar-music/detail/${logId}`
          })
        }
      } catch (err) {
        console.error(err)
        this.clearPoll()
        this.$message.error((err && err.message) || '解析失败，请重试')
        this.resetIdle()
      }
    },
    uploadToQiniu(file, token, key) {
      return new Promise((resolve, reject) => {
        const formData = new FormData()
        formData.append('token', token)
        formData.append('key', key)
        formData.append('file', file)

        const xhr = new XMLHttpRequest()
        this.uploadXhr = xhr

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable && e.total > 0) {
            this.uploadPercent = Math.min(99, Math.round((e.loaded / e.total) * 100))
          }
        }

        xhr.onerror = () => {
          reject(new Error('网络异常，上传失败'))
        }

        xhr.onabort = () => {
          reject(new Error('上传已取消'))
        }

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const res = JSON.parse(xhr.responseText || '{}')
              resolve(res)
            } catch (e) {
              resolve({ key })
            }
          } else {
            let message = '上传失败，请重试'
            try {
              const res = JSON.parse(xhr.responseText || '{}')
              if (res.error) message = res.error
            } catch (e) {
              // ignore
            }
            reject(new Error(message))
          }
        }

        xhr.open('POST', QINIU_UPLOAD_URL, true)
        xhr.send(formData)
      })
    },
    triggerUpload() {
      if (!this.isIdle) return
      if (!this.agreed) {
        this.$message.warning('请先同意用户服务协议')
        return
      }
      this.$refs.fileInput && this.$refs.fileInput.click()
    },
    onFileChange(e) {
      const file = e.target.files && e.target.files[0]
      this.handleFile(file)
      e.target.value = ''
    },
    onDragEnter() {
      if (!this.isIdle) return
      this.dragCounter += 1
      this.isDragover = true
    },
    onDragOver() {
      if (!this.isIdle) return
      this.isDragover = true
    },
    onDragLeave() {
      if (!this.isIdle) return
      this.dragCounter = Math.max(0, this.dragCounter - 1)
      if (this.dragCounter === 0) {
        this.isDragover = false
      }
    },
    onDrop(e) {
      if (!this.isIdle) return
      this.dragCounter = 0
      this.isDragover = false
      const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]
      this.handleFile(file)
    },
    handleSubmit() {
      if (!this.isIdle) return
      const keyword = (this.keyword || '').trim()
      if (!keyword) {
        this.$message.warning('请输入关键字进行搜索')
        return
      }
      this.$router.push({
        path: '/search-result',
        query: {
          page: 1,
          type: 2,
          keyword_name: keyword
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.upload-panel {
  width: 100%;
  max-width: 880px;
  padding: 0 24px;
  box-sizing: border-box;

  &__header {
    margin-bottom: 28px;
    text-align: center;
  }

  &__title {
    margin: 0 0 10px;
    font-size: 28px;
    font-weight: 600;
    color: #1a1a1a;
    line-height: 1.4;
  }

  &__title-accent {
    color: #e52a0d;
  }

  &__size-error {
    margin: 0 0 12px;
    font-size: 14px;
    color: #e52a0d;
    line-height: 1.4;
    text-align: left;
  }

  &__box {
    position: relative;
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 12px;
    padding: 20px 20px 16px;
    box-sizing: border-box;
    transition: border-color 0.2s, box-shadow 0.2s;
    min-height: 200px;

    &.is-dragover {
      border-color: #e52a0d;
      box-shadow: 0 0 0 2px rgba(229, 42, 13, 0.12);
    }

    &.is-uploading {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 36px 24px;
    }
  }

  &__input {
    display: block;
    width: 100%;
    min-height: 120px;
    padding: 0;
    margin: 0;
    border: none;
    outline: none;
    resize: none;
    font-size: 14px;
    line-height: 1.6;
    color: #1a1a1a;
    background: transparent;
    box-sizing: border-box;
    font-family: inherit;

    &::placeholder {
      color: #bbb;
    }
  }

  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 16px;
  }

  &__action-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    font-size: 13px;
    color: #666;
    background: #f5f5f5;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    outline: none;

    i {
      font-size: 14px;
    }

    &:hover {
      color: #e52a0d;
      background: rgba(229, 42, 13, 0.06);
    }
  }

  &__submit {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: #e52a0d;
    color: #fff;
    cursor: pointer;
    outline: none;
    flex-shrink: 0;
    box-shadow: 0 4px 12px rgba(229, 42, 13, 0.28);

    &:hover {
      background: #c9240b;
    }
  }

  &__wave-icon {
    width: 22px;
    height: 22px;
  }

  &__file {
    display: none;
  }

  &__progress {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  &__file-badge {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    padding: 8px 16px 8px 8px;
    background: #f5f5f5;
    border-radius: 999px;
    box-sizing: border-box;
  }

  &__file-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    margin-right: 8px;
    flex-shrink: 0;
    background: #fff;
    border-radius: 6px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);

    svg {
      width: 18px;
      height: 18px;
    }
  }

  &__file-name {
    font-size: 14px;
    color: #333;
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 360px;
  }

  &__status-row {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 20px;
  }

  &__spinner {
    font-size: 18px;
    color: #e52a0d;

    &--done {
      color: #e52a0d;
    }
  }

  &__status-text {
    font-size: 14px;
    color: #666;
    line-height: 1.4;
  }

  &__hint {
    margin: 12px 0 0;
    font-size: 13px;
    color: #999;
    line-height: 1.5;

    em {
      font-style: normal;
      color: #e52a0d;
      font-weight: 600;
    }
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 16px;
  }

  &__agree {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: #666;
    cursor: pointer;

    /deep/ .el-checkbox__input.is-checked .el-checkbox__inner {
      background-color: #e52a0d;
      border-color: #e52a0d;
    }

    /deep/ .el-checkbox__inner:hover {
      border-color: #e52a0d;
    }
  }

  &__tips {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 0;
    border: none;
    background: transparent;
    font-size: 13px;
    color: #666;
    cursor: pointer;
    outline: none;

    &:hover {
      color: #e52a0d;
    }
  }

  &__tips-list {
    margin: 0;
    padding-left: 20px;
    font-size: 14px;
    line-height: 1.7;
    color: #333;

    li + li {
      margin-top: 16px;
    }
  }
}
</style>

<style lang="scss">
.upload-panel-tips-dialog {
  .el-dialog__header {
    position: relative;
    margin: 0;
    padding: 14px 48px;
    text-align: center;
    background: #f7f7f7;
    border-bottom: 1px solid #ebebeb;
    border-radius: 4px 4px 0 0;
  }

  .el-dialog__headerbtn {
    top: 50%;
    right: 16px;
    transform: translateY(-50%);
  }

  .el-dialog__title {
    font-size: 16px;
    font-weight: 600;
    color: #1a1a1a;
    line-height: 1.4;
  }

  .el-dialog__body {
    padding: 16px 20px 6px;
  }

  .el-dialog__footer {
    .el-button--primary {
      background-color: #e52a0d;
      border-color: #e52a0d;

      &:hover,
      &:focus {
        background-color: #c9240b;
        border-color: #c9240b;
      }
    }
  }
}
</style>
